'use client';

import { useEffect, useState } from 'react';
import Script from 'next/script';
import { CONSENT_STORAGE_KEY, CONSENT_EVENT, type ConsentValue } from '@/lib/consent';

interface GoogleAnalyticsLoaderProps {
  googleAnalyticsId: string;
}

// Renders the GA4 script tags only once the visitor has actively accepted
// analytics cookies via CookieConsent — never on first render, and never
// just because googleAnalyticsId is configured. Previously these tags were
// server-rendered unconditionally in app/[locale]/layout.tsx, which set
// GA4's _ga / _ga_<id> cookies on every visitor before they'd made any
// choice, contradicting the privacy page's claim that public pages don't
// write persistent tracking cookies. This is the fix for that: no consent,
// no script, no cookie.
export default function GoogleAnalyticsLoader({ googleAnalyticsId }: GoogleAnalyticsLoaderProps) {
  const [consented, setConsented] = useState(false);

  useEffect(() => {
    const readConsent = () => {
      try {
        return localStorage.getItem(CONSENT_STORAGE_KEY) as ConsentValue | null;
      } catch {
        return null;
      }
    };

    if (readConsent() === 'accepted') setConsented(true);

    // CookieConsent dispatches this on the same tab when the visitor clicks
    // Accept, so GA4 can start loading immediately without a page reload —
    // a storage event alone wouldn't fire in the tab that made the change.
    const onConsentChange = (e: Event) => {
      const detail = (e as CustomEvent<ConsentValue>).detail;
      setConsented(detail === 'accepted');
    };
    window.addEventListener(CONSENT_EVENT, onConsentChange);
    return () => window.removeEventListener(CONSENT_EVENT, onConsentChange);
  }, []);

  if (!consented) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleAnalyticsId)}`}
        strategy="afterInteractive"
        async
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', ${JSON.stringify(googleAnalyticsId).replace(/</g, '\\u003c')}, {
            'allow_google_signals': false
          });
        `}
      </Script>
    </>
  );
}
